/** The whole router.
 *
 * Nudge is one screen with a tab bar and does not need routing — except for the
 * handful of URLs that arrive from outside the app: the staff console, which
 * gets bookmarked and linked to colleagues, and the three landing paths that
 * come out of emails (`/verify`, `/reset`, `/join`). Those have to survive a
 * cold load in whatever browser the mail client opens.
 *
 * `app/public/.htaccess` serves `index.html` for any non-file path and the Vite
 * dev server does the same, so a hard refresh on any of them works.
 *
 * Lives apart from `adminRoute.ts` because email links are not a staff concern
 * and the console should not be the thing everything else imports history
 * handling from.
 */

import { useEffect, useState } from 'react'

/** A same-tab navigation the router should react to. `popstate` only fires for
 * back/forward, so pushState needs its own signal. */
const NAV_EVENT = 'nudge:navigate'

export function navigate(to: string): void {
  if (window.location.pathname + window.location.search === to) return
  window.history.pushState({}, '', to)
  window.dispatchEvent(new Event(NAV_EVENT))
}

export function usePathname(): string {
  const [path, setPath] = useState(() => window.location.pathname)
  useEffect(() => {
    const sync = () => setPath(window.location.pathname)
    window.addEventListener('popstate', sync)
    window.addEventListener(NAV_EVENT, sync)
    return () => {
      window.removeEventListener('popstate', sync)
      window.removeEventListener(NAV_EVENT, sync)
    }
  }, [])
  return path
}
